import React, { useCallback, useEffect, useId, useState } from "react";
import { Copy, MailPlus, RefreshCw, X } from "lucide-react";
import api from "../../../services/api";
import { ConfirmDialog, Dialog, EmptyState, Loading, errorText, formatDate, fullName, useToast } from "./common";

const EMPTY = { name: "", surname: "", email: "" };

const InviteForm = ({ onDone }) => {
  const id = useId();
  const { notify } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/admins/invite", {
        name: form.name.trim(),
        surname: form.surname.trim(),
        email: form.email.trim(),
      });
      setForm(EMPTY);
      notify(data.invite_sent ? `Invitation sent to ${data.admin.email}.` : "Admin added. Email isn't set up, so share the link below.");
      onDone(data);
    } catch (err) {
      setError(errorText(err, "Couldn't send the invitation."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-form" onSubmit={submit} noValidate>
      <div className="eg-field">
        <label className="eg-label" htmlFor={`${id}-name`}>First name</label>
        <input id={`${id}-name`} className="eg-input" value={form.name} onChange={set("name")} maxLength={60} autoComplete="off" />
      </div>
      <div className="eg-field">
        <label className="eg-label" htmlFor={`${id}-surname`}>Last name (optional)</label>
        <input id={`${id}-surname`} className="eg-input" value={form.surname} onChange={set("surname")} maxLength={60} autoComplete="off" />
      </div>
      <div className="eg-field">
        <label className="eg-label" htmlFor={`${id}-email`}>Email</label>
        <input id={`${id}-email`} type="email" className="eg-input" value={form.email} onChange={set("email")} maxLength={120} autoComplete="off" />
        <p className="admin-field-hint">They get an email with a link to set their own password. The link works for 3 days.</p>
      </div>
      {error && <p className="admin-error" role="alert">{error}</p>}
      <button type="submit" className="eg-btn eg-btn-gold" disabled={!valid || saving}>
        <MailPlus size={16} /> {saving ? "Sending…" : "Send invitation"}
      </button>
    </form>
  );
};

const LinkNotice = ({ link, onClose }) => {
  const { notify } = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      notify("Link copied.");
    } catch {
      notify("Couldn't copy. Select the link and copy it.", "error");
    }
  };
  return (
    <div className="admin-banner" role="status">
      <p>Email isn't configured on the server, so no email was sent. Give this link to the new admin (it works once, for 3 days):</p>
      <input className="eg-input" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Invitation link" />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button type="button" className="eg-btn eg-btn-outline" onClick={copy}><Copy size={14} /> Copy link</button>
        <button type="button" className="eg-btn eg-btn-outline" onClick={onClose}><X size={14} /> Dismiss</button>
      </div>
    </div>
  );
};

const AdminsPanel = () => {
  const { notify } = useToast();
  const [admins, setAdmins] = useState(null);
  const [error, setError] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data.admins || []);
      setError(null);
    } catch (err) {
      setError(errorText(err, "Couldn't load administrators."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = async (a) => {
    try {
      const { data } = await api.post(`/admin/admins/${a.user_id}/resend-invite`);
      if (data.invite_link) setLink(data.invite_link);
      notify(data.invite_sent ? `Invitation re-sent to ${a.email}.` : "Email isn't set up. Share the link shown.");
      load();
    } catch (err) {
      notify(errorText(err, "Couldn't resend the invitation."), "error");
    }
  };

  const cancel = async () => {
    setBusy(true);
    setCancelError(null);
    try {
      await api.delete(`/admin/admins/${cancelTarget.user_id}`);
      notify("Invitation cancelled.");
      setCancelTarget(null);
      load();
    } catch (err) {
      setCancelError(errorText(err, "Couldn't cancel the invitation."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="admin-section-head">
        <h3>Administrators</h3>
        <button type="button" className="eg-btn eg-btn-gold" onClick={() => setInviting(true)}>
          <MailPlus size={16} /> Invite admin
        </button>
      </div>

      {link && <LinkNotice link={link} onClose={() => setLink(null)} />}
      {error && <p className="admin-error" role="alert">{error}</p>}
      {!admins && !error && <Loading />}
      {admins && admins.length === 0 && <EmptyState title="No administrators yet" />}
      {admins && admins.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Status</th><th>Added</th><th><span className="sr-only">Actions</span></th></tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.user_id}>
                  <td>{fullName(a)}</td>
                  <td>{a.email}</td>
                  <td>
                    {a.status === "invited"
                      ? <span className="admin-badge admin-badge-gold">Invite pending</span>
                      : <span className="admin-badge admin-badge-success">Active</span>}
                    {a.is_suspended && <span className="admin-badge admin-badge-danger">Suspended</span>}
                  </td>
                  <td>{formatDate(a.invited_at || a.created_at)}{a.invited_by ? ` · by ${a.invited_by}` : ""}</td>
                  <td>
                    {a.status === "invited" && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button type="button" className="eg-btn eg-btn-outline" onClick={() => resend(a)}><RefreshCw size={14} /> Resend</button>
                        <button type="button" className="eg-btn eg-btn-outline" onClick={() => { setCancelError(null); setCancelTarget(a); }}>Cancel</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={inviting} title="Invite an administrator" subtitle="They'll choose their own password." onClose={() => setInviting(false)}>
        <InviteForm onDone={(data) => { setInviting(false); if (data.invite_link) setLink(data.invite_link); load(); }} />
      </Dialog>

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title="Cancel this invitation?"
        confirmLabel="Cancel invitation"
        busy={busy}
        error={cancelError}
        onConfirm={cancel}
        onCancel={() => setCancelTarget(null)}
      >
        {cancelTarget && <>The invitation for <strong>{cancelTarget.email}</strong> will stop working and their pending account is removed.</>}
      </ConfirmDialog>
    </section>
  );
};

export default AdminsPanel;
